/**
 * catchしたエラーを渡して、エラーメッセージを得る
 */

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export const pickErrorMessages = (error: any): Array<string> => {
  if (!error) {
    return ['エラーが発生しました'];
  }

  // APIエラー
  if (error.errors && error.errors.length !== 0) {
    return Object.entries(error.errors).flatMap((obj) => {
      return obj.at(1);
    }) as Array<string>;
  }

  return [error.message];
};

/**
 * ページ遷移
 */
export const locationHref = (url: string, newTab: boolean) => {
  if (newTab) {
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    (window as any).open('about:blank', '_blank').location.href = url;
  } else {
    location.href = url;
  }
};

/**
 * ページ最上部へ瞬時にスクロールする
 *
 * ページ全体のスクロールコンテナはブラウザーによって異なるため、4経路すべてを指定する。
 * 本リポジトリは reset.scss で body に overflow: scroll を敷いており body 側も対象になり得る。
 *
 * なめらかなスクロール（behavior: 'smooth'）は受け付けない。scrollTop への代入（3・4番目）は
 * 常に瞬時スクロールになるため、引数で受け取っても最後の代入で打ち消され意味を持たないため。
 * なめらかに動かしたい用途が出た場合は、代入を使わない別の関数として用意する。
 */
export const scrollToPageTop = () => {
  if (typeof window === 'undefined') return;

  document.scrollingElement?.scrollTo({ top: 0, behavior: 'auto' });
  window.scrollTo({ top: 0, behavior: 'auto' });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
};

/**
 * 任意の要素までスクロール
 */
export const scrollIntoTheView = (id: string) => {
  const element = document.getElementById(id) as HTMLElement;
  if (!element) return;

  const position = element.getBoundingClientRect();
  window.scrollTo({
    left: 0,
    top: position.top + window.scrollY - 100, //ヘッダー分ずらす
    behavior: 'smooth',
  });
};

/** 未ログインを表すHTTPステータスコード */
const HTTP_STATUS_UNAUTHORIZED = 401;

/**
 * 未ログインによるAPIエラーかどうかを判定する。
 *
 * バックエンドは未認証・退会・ユーザーステータス変更のいずれの場合も HTTP 401 を返す。
 * レスポンスボディの code は汎用エラーの 1 で、HTTP 404 / 400 / 500 と共通のため判定には使えない。
 *
 * 前提: 呼び出し側アプリの http() が、reject する値に HTTP ステータスを `status` として
 * 付与していること。付与していないアプリでは常に false を返すため、利用前に確認する。
 * 現時点で付与しているのは apps/raffle のみ（例: `Promise.reject({ ...responseJson, status: res.status })`）。
 * 他アプリはレスポンスボディだけを reject するため対象外。
 */
export const isUnauthorizedError = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && (error as { status?: number }).status === HTTP_STATUS_UNAUTHORIZED;

/** URL経由で受け渡すエラーメッセージの最大文字数 */
const SAFE_ERROR_MESSAGE_MAX_LENGTH = 200;

/** 制御文字（タブ・改行を含む C0 制御文字と DEL） */
// biome-ignore lint/suspicious/noControlCharactersInRegex: 制御文字の除去が目的
const CONTROL_CHARACTERS = /[ -]/g;

/**
 * URL経由で受け渡すエラーメッセージを無害化する。
 *
 * クエリパラメータは利用者が自由に組み立てられるため、受け取った文字列をそのまま
 * モーダルに表示すると任意の文言を出せてしまう。制御文字を除去し、長さを制限する。
 * 表示側は必ずテキストとして描画すること（HTMLとして描画するとスクリプトが実行される）。
 */
export const toSafeErrorMessage = (error: unknown): string => {
  const [firstMessage] = pickErrorMessages(error);
  // APIのerrorsが入れ子になっている場合など、文字列でない値が混ざっても文言が壊れないようにする
  const rawMessage = typeof firstMessage === 'string' ? firstMessage : 'エラーが発生しました';
  return rawMessage.replace(CONTROL_CHARACTERS, '').slice(0, SAFE_ERROR_MESSAGE_MAX_LENGTH);
};
